import { synthDocumentContentHash } from "./compiler";
import { parseSynthDocument, type SynthDocument } from "./document";

export type DesignDocumentStorage = Pick<Storage, "getItem" | "setItem"> &
  Partial<Pick<Storage, "removeItem">>;

export const PUBLISHED_DESIGN_STORAGE_PREFIX = "patchwork:synth-published:";

export type PublishedDesignSnapshot = {
  version: 1;
  publishedAt: string;
  contentHash: string;
  document: SynthDocument;
};

export function publishedDesignStorageKey(definitionId: string) {
  return PUBLISHED_DESIGN_STORAGE_PREFIX + definitionId;
}

export function readPublishedDesign(
  definitionId: string,
  storage: DesignDocumentStorage | undefined,
): { snapshot?: PublishedDesignSnapshot; warning?: string } {
  if (!storage) return {};
  try {
    const serialized = storage.getItem(publishedDesignStorageKey(definitionId));
    if (!serialized) return {};
    const value = JSON.parse(serialized) as {
      version?: unknown;
      publishedAt?: unknown;
      contentHash?: unknown;
      document?: unknown;
    };
    if (
      value.version !== 1 ||
      typeof value.publishedAt !== "string" ||
      typeof value.contentHash !== "string"
    ) {
      throw new Error("Unsupported published-design snapshot.");
    }
    const document = parseSynthDocument(value.document);
    if (synthDocumentContentHash(document) !== value.contentHash) {
      throw new Error("Published-design checksum mismatch.");
    }
    return {
      snapshot: {
        version: 1,
        publishedAt: value.publishedAt,
        contentHash: value.contentHash,
        document,
      },
    };
  } catch {
    return { warning: "The published design could not be read and has been ignored." };
  }
}

export function loadPublishedDesignSnapshot(
  definitionId: string,
  storage: DesignDocumentStorage | undefined,
) {
  return readPublishedDesign(definitionId, storage).snapshot;
}
